class GiftSubscriptionDrawer extends HTMLElement {
  /**
   * @type {Object.<string, Pricing>}
   */
  pricing = {};
  activeElement = null;
  formId = '';
  launchedFromQueryParam = false;

  constructor() {
    super();
    this.getData();
    this.setupVariantChangeHandler();
    this.setupAddToBagHandler();
    this.setupCloseHandlers();
    this.checkQueryParam();
  }

  /**
   * Captures data from the dataset
   */
  getData() {
    this.formId = this.dataset.formId;
    const variants = JSON.parse(this.dataset.variants);
    for (const variant of variants) {
      this.pricing[variant.title] = {
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        id: variant.id,
      };
    }
  }

  /**
   * Opens the drawer
   * @param {HTMLElement} triggeredBy The element that triggered the opening of the drawer
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.gift-subscription-drawer__inner');
        const focusElement = this.querySelector('.gift-subscription-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  /**
   * Closes the drawer
   */
  close() {
    this.classList.remove('active');
    if (this.activeElement) removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    const formEl = this.querySelector('product-gift-subscription');
    if (formEl) {
      setTimeout(() => {
        formEl.togglePrintAtHome(false);
      });
    }
  }

  /**
   * Sets up event listeners for closing the drawer via the overlay and escape key
   */
  setupCloseHandlers() {
    this.querySelector('.gift-subscription-drawer__overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  /**
   * Sets the active element for focus trapping
   * @param {HTMLElement} element The element to set as active
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Add an event listener to the variant selector
   */
  setupVariantChangeHandler() {
    const fieldsetEl = this.querySelector('variant-selects fieldset');
    if (fieldsetEl) {
      fieldsetEl.addEventListener('change', this.handleVariantChange.bind(this));
    }
  }

  /**
   * Handles the change event on the variant selector
   */
  handleVariantChange(evt) {
    this.updatePricing(evt.target.value);
    this.updateProductForm(evt.target.value);
  }

  /**
   * Add an event listener to the add to bag button
   */
  setupAddToBagHandler() {
    const buttonEl = this.querySelector(`#${this.formId} [type="submit"]`);
    if (buttonEl) {
      buttonEl.addEventListener('click', this.handleAddToBag.bind(this));
    }
  }

  /**
   * Closes the drawer when the add to bag button is clicked
   * @param {Event} evt The event object
   */
  handleAddToBag() {
    const formEl = this.querySelector(`#${this.formId}`);
    if (formEl && formEl.checkValidity()) {
      this.close();
    }
  }

  /**
   * Returns a formatted price string
   * @param {number} price The price to format
   * @returns {string} The formatted price
   */
  formatPrice(price) {
    const p = (price / 100).toFixed(2);
    try {
      const locale = `${window.Shopify.locale}-${window.Shopify.country}`;
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: window.Shopify.currency.active });
      return formatter.format(p);
    } catch {
      return `$${p}`;
    }
  }

  /**
   * Updates the pricing information for the selected variant
   * @param {string} variantName The name of the selected variant
   */
  updatePricing(variantName) {
    const originalPriceEl = this.querySelector('.gift-subscription-drawer__price-original');
    const adjustedPriceEl = this.querySelector('.gift-subscription-drawer__price-adjusted');
    if (originalPriceEl) {
      const originalPrice = this.pricing[variantName].compare_at_price;
      originalPriceEl.style.display = originalPrice > 0 ? 'inline' : 'none';
      originalPriceEl.innerHTML = this.formatPrice(originalPrice);
    }
    if (adjustedPriceEl) {
      adjustedPriceEl.innerHTML = this.formatPrice(this.pricing[variantName].price);
    }
  }

  /**
   * Updates the product form with the selected variant
   * @param {string} variantName The name of the selected variant
   */
  updateProductForm(variantName) {
    const formEl = this.querySelector(`#${this.formId}`);
    if (!formEl) return;
    const inputEl = formEl.querySelector('input[name="id"]');
    if (inputEl) {
      inputEl.value = this.pricing[variantName].id;
    }
  }

  /**
   * Checks the query params to see if the drawer should default to open
   */
  checkQueryParam() {
    const urlParams = new URLSearchParams(window.location.search);
    this.launchedFromQueryParam = false;
    if (urlParams.has('gift')) {
      this.launchedFromQueryParam = true;
      this.open();
      urlParams.delete('gift');
      const newParams = urlParams.toString();
      const newUrl = `${window.location.pathname}${newParams ? '?' + newParams : ''}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }
}

if (!customElements.get('gift-subscription-drawer')) {
  customElements.define('gift-subscription-drawer', GiftSubscriptionDrawer);
}

/**
 * @typedef Pricing
 * @property {number} price
 * @property {number} compare_at_price
 * @property {string} id
 */
